// libs
import React, { useCallback, useMemo, useState } from 'react'

// components
import InputArea from 'src/components/forms/input_area/input_area'
import InputField from 'src/components/forms/input_field/input_field'
import Modal from 'src/components/modal/modal'

// constants
import { CLASS_PREFIX } from 'src/constants'

// local components
import { ProjectCreateModalHeader } from 'src/components/projects/project_create/project_create_modal_header'

// styles
import {
  ProjectFragmentFragment,
  useCreatePublicParticipationProject,
  useTags,
  useUpdatePublicParticipationProject
} from '@lopo-tech/lopo-api'
import classNames from 'classnames'
import { MultiSelectField } from 'src/components/forms/multi-select-field/multi-select-field'
import Uploader from 'src/components/uploader/uploader'
import { textResources } from 'src/lang/de'
import mediaSelectImage from 'src/post-creation-wizard/wizards/common/media_select/media_select.svg'
import { useCreateAssetFlow } from 'src/utils/assets/createAsset'
import '../project_modal.scss'

const MIN_SEARCH_LENGTH = 3

type IProps = {
  onCancel: () => void;
  onNext: (projectSlug?: string) => void;

  /** If provided, the modal will prefill and update instead of create. */
  prefill?: Pick<
    ProjectFragmentFragment,
    'id' | 'tags' | 'name' | 'content' | 'images'
  >;
};

const cls = `${CLASS_PREFIX}project-modal`

const t = textResources.projects

const useCreateProject = () => {
  const [createProject] = useCreatePublicParticipationProject()
  const createAssetFlow = useCreateAssetFlow()

  return useCallback(
    async (
      name: string,
      body: string,
      tagIds: string[],
      coverImageSrc?: string
    ) => {
      let assetIds: string[] = []

      if (coverImageSrc) {
        assetIds = await createAssetFlow([coverImageSrc])
      }

      const response = await createProject({
        variables: {
          input: {
            name,
            body,
            assetIds,
            tagIds
          }
        }
      })

      return response.data?.createPublicParticipationProject
    },
    [createProject, createAssetFlow]
  )
}

const useUpdateProject = () => {
  const [updateProject] = useUpdatePublicParticipationProject()
  const createAssetFlow = useCreateAssetFlow()

  return useCallback(
    async (
      projectId: string,
      name: string,
      body: string,
      tagIds: string[],
      coverImageSrc?: string,
      initalImage?: ProjectFragmentFragment['images'][number]
    ) => {
      let assetIds: string[] | undefined

      const coverImageChanged =
        !!coverImageSrc && coverImageSrc !== initalImage?.file.previewUrl
      if (coverImageChanged) {
        assetIds = await createAssetFlow([coverImageSrc])
      }

      const response = await updateProject({
        variables: {
          input: {
            id: projectId,
            name,
            body,
            tagIds,
            assetIds
          }
        }
      })

      return response.data?.updatePublicParticipationProject
    },
    [updateProject, createAssetFlow]
  )
}

export const ProjectsCreateModal: React.FC<IProps> = ({
  onCancel,
  onNext,
  prefill
}) => {
  // tags
  const [tagSearchValue, setTagSearchValue] = useState('')
  const { data: tagList } = useTags({
    variables: { filters: { nameCont: tagSearchValue }, page: 1, perPage: 100 },
    skip: tagSearchValue.length < MIN_SEARCH_LENGTH
  })

  const [imageSrc, setImageSrc] = useState<string | undefined>(
    prefill?.images[0]?.file.previewUrl
  )

  const [name, setName] = useState(prefill?.name ?? '')
  const [body, setBody] = useState(prefill?.content?.text || '')
  const [selectedTags, setSelectedTags] = useState(prefill?.tags ?? [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const createProject = useCreateProject()
  const updateProject = useUpdateProject()

  const tagIds = useMemo(() => selectedTags.map((t) => t.id), [selectedTags])

  const canSubmit = useMemo(
    () => name.trim().length > 0 && body.trim().length > 0 && !isSubmitting,
    [name, body, isSubmitting]
  )

  const isEditMode = !!prefill?.id

  return (
    <Modal autoSize customClassNames={`${cls}__wizard-body`} hideCloseButton>
      <>
        <ProjectCreateModalHeader />

        <div className={`${cls}__wizard-details`}>
          {/* Left: cover photo */}
          <div className={`${cls}__cover`}>
            <div className={`${cls}__cover-label`}>
              {t.createProjectModal.coverPhotoOptional}
            </div>
            <Uploader
              onImageAdded={(images) => {
                setImageSrc(images[0])
              }}
              multiple={false}
            >
              <div
                className={classNames(`${cls}__cover-image`, {
                  [`${cls}__cover-image_selected`]: !!imageSrc
                })}
              >
                <img src={imageSrc ?? mediaSelectImage} alt="" loading="lazy" />
              </div>
              <div className={`${cls}__cover-foot`}>
                <button type="button" className={`${cls}__browse-btn`}>
                  {t.createProjectModal.browseFiles}
                </button>
              </div>
            </Uploader>
          </div>

          {/* Right: details form */}
          <div className={`${cls}__form`}>
            <div className={`${cls}__form-row`}>
              <InputField
                name="projName"
                label={t.createProjectModal.projectName}
                value={name}
                onChange={setName}
              />
            </div>

            <div className={`${cls}__form-row`}>
              <InputArea
                name="projAbout"
                label={t.createProjectModal.aboutProject}
                initialRows={4}
                value={body}
                onChange={setBody}
              />
            </div>

            <div className={`${cls}__form-row`}>
              <MultiSelectField
                label={t.createProjectModal.addTags}
                name="tags"
                options={tagList?.tags.items ?? []}
                values={selectedTags}
                onTextChange={setTagSearchValue}
                onChange={setSelectedTags}
              />
            </div>
          </div>
        </div>

        <div className={`${cls}__wizard-actions`}>
          <button type="button" className={`${cls}__back`} onClick={onCancel}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className={`${cls}__submit`}
            disabled={!canSubmit}
            onClick={async () => {
              if (!name.trim() || !body.trim()) {
                return
              }

              try {
                setIsSubmitting(true)

                const response = isEditMode
                  ? await updateProject(
                    prefill.id,
                    name.trim(),
                    body.trim(),
                    tagIds,
                    imageSrc,
                    prefill?.images[0]
                  )
                  : await createProject(
                    name.trim(),
                    body.trim(),
                    tagIds,
                    imageSrc
                  )

                if (!response?.errors.length) {
                  onNext(response?.project?.slug)
                } else {
                  alert(t.createProjectModal.errorGeneric)
                }
              } catch {
                alert(t.createProjectModal.errorGeneric)
              } finally {
                setIsSubmitting(false)
              }
            }}
          >
            {isEditMode ? t.common.update : t.common.create}
          </button>
        </div>
      </>
    </Modal>
  )
}
